import React from "react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Card, CardContent } from "./ui/card";
import { 
  X,
  Star,
  Package,
  DollarSign,
  Tag,
  Ruler,
  Weight
} from "lucide-react";

const ProductPreview = ({ product, onClose, onEdit }) => {
  const formatPrice = (price) => {
    const numPrice = parseFloat(price || 0);
    return numPrice > 0 ? `$${numPrice.toFixed(2)}` : 'Price on request';
  };

  const primaryImage = product.images && product.images.length > 0 
    ? product.images[0].src 
    : "https://placehold.co/400/400";

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-2xl font-bold text-gray-900">Product Preview</h2>
          <div className="flex items-center gap-2">
            <Button onClick={onEdit} size="sm">
              Edit Product
            </Button>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Product Images */}
            <div className="space-y-4">
              <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden">
                <img
                  src={primaryImage}
                  alt={product.name}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.src = "https://placehold.co/400/400";
                  }}
                />
              </div>
              
              {product.images && product.images.length > 1 && (
                <div className="grid grid-cols-4 gap-2">
                  {product.images.slice(1, 5).map((image, index) => (
                    <div key={index} className="aspect-square bg-gray-100 rounded overflow-hidden">
                      <img
                        src={image.src}
                        alt={`${product.name} ${index + 2}`}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.src = "https://placehold.co/100/100";
                        }}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Product Details */}
            <div className="space-y-6">
              {/* Basic Info */}
              <div>
                <div className="flex items-start justify-between mb-2">
                  <h1 className="text-3xl font-bold text-gray-900">{product.name}</h1>
                  {product.featured && (
                    <Badge className="bg-yellow-600 hover:bg-yellow-700 text-white">
                      <Star className="w-3 h-3 mr-1" />
                      FEATURED
                    </Badge>
                  )}
                </div>
                
                {product.sku && (
                  <p className="text-sm text-gray-500 font-mono mb-4">
                    SKU: {product.sku}
                  </p>
                )}
              </div>

              {/* Pricing */}
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-purple-600" />
                  <span className="text-2xl font-bold text-purple-600">
                    {formatPrice(product.price || product.regular_price)}
                  </span>
                  {product.sale_price && parseFloat(product.sale_price) > 0 && (
                    <span className="text-lg text-gray-500 line-through">
                      {formatPrice(product.regular_price)}
                    </span>
                  )}
                </div>
              </div>

              {/* Stock Status */}
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Package className="w-4 h-4 text-gray-500" />
                  <Badge 
                    className={
                      product.stock_status === 'instock' 
                        ? "bg-green-100 text-green-800 border-green-200"
                        : product.stock_status === 'outofstock'
                        ? "bg-red-100 text-red-800 border-red-200"
                        : "bg-yellow-100 text-yellow-800 border-yellow-200"
                    }
                  >
                    {product.stock_status === 'instock' && 'In Stock'}
                    {product.stock_status === 'outofstock' && 'Out of Stock'}
                    {product.stock_status === 'onbackorder' && 'On Backorder'}
                  </Badge>
                </div>
                
                {product.stock_quantity && (
                  <span className="text-sm text-gray-500">
                    {product.stock_quantity} available
                  </span>
                )}
              </div>

              {/* Categories */}
              {product.categories && product.categories.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Tag className="w-4 h-4 text-gray-500" />
                    <span className="text-sm font-medium text-gray-700">Categories</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {product.categories.map((category) => (
                      <Badge 
                        key={category.id} 
                        variant="outline" 
                        className="text-purple-600 border-purple-200"
                      >
                        {category.name}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Descriptions */}
              <div className="space-y-4">
                {product.short_description && (
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Short Description</h3>
                    <div 
                      className="text-gray-700 prose prose-sm max-w-none"
                      dangerouslySetInnerHTML={{ __html: product.short_description }}
                    />
                  </div>
                )}

                {product.description && (
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Full Description</h3>
                    <div 
                      className="text-gray-700 prose prose-sm max-w-none"
                      dangerouslySetInnerHTML={{ __html: product.description }}
                    />
                  </div>
                )}
              </div>

              {/* Physical Properties */}
              {(product.weight || (product.dimensions && (product.dimensions.length || product.dimensions.width || product.dimensions.height))) && (
                <Card>
                  <CardContent className="p-4">
                    <h3 className="font-semibold text-gray-900 mb-3">Physical Properties</h3>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      {product.weight && (
                        <div className="flex items-center gap-2">
                          <Weight className="w-4 h-4 text-gray-500" />
                          <span className="text-gray-700">Weight: {product.weight}</span>
                        </div>
                      )}
                      
                      {product.dimensions && (product.dimensions.length || product.dimensions.width || product.dimensions.height) && (
                        <div className="flex items-center gap-2">
                          <Ruler className="w-4 h-4 text-gray-500" />
                          <span className="text-gray-700">
                            Dimensions: {product.dimensions.length || '0'} × {product.dimensions.width || '0'} × {product.dimensions.height || '0'}
                          </span>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductPreview;